import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
	selector: '[micrFormat]',
	standalone: true,
})
export class MicrFormatDirective {
	previousValue = '';

	private readonly micrPattern = '^[0-9a-d]*$';

	/**
	 * Class constructor
	 *
	 * @param hostElement
	 */
	constructor(private readonly hostElement: ElementRef) {}

	/**
	 * Event handler for host's change event
	 *
	 * @param e
	 */
	@HostListener('change', ['$event']) onChange(e: Event): void {
		const value = (e?.target as HTMLInputElement)?.value || this.hostElement.nativeElement.value;
		this.validateValue(value);
	}

	/**
	 * Event handler for host's paste event
	 *
	 * @param e
	 */
	@HostListener('paste', ['$event']) onPaste(e: ClipboardEvent): void {
		// get and validate data from clipboard
		const value = e.clipboardData?.getData('text/plain') || '';
		this.validateValue(value);
		e.preventDefault();
		// trigger input event to update model
		this.hostElement.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
	}

	/**
	 * Event handler for host's keydown event
	 *
	 * @param event
	 */
	@HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
		const originalValue: string = (e.target as HTMLInputElement).value;
		const key: string = e.key;
		const controlOrCommand = e.ctrlKey === true || e.metaKey === true;

		// allowed keys apart from MICR characters
		const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Escape', 'Tab'];

		// allow some non-MICR hot-keys
		if (
			allowedKeys.indexOf(key) !== -1 ||
			// Allow: Ctrl+A and Command+A
			(key === 'a' && controlOrCommand) ||
			// Allow: Ctrl+C and Command+C
			(key === 'c' && controlOrCommand) ||
			// Allow: Ctrl+V and Command+V
			(key === 'v' && controlOrCommand) ||
			// Allow: Ctrl+X and Command+X
			(key === 'x' && controlOrCommand)
		) {
			// let it happen, don't do anything
			return;
		}

		// save value before keydown event
		this.previousValue = originalValue;

		// allow MICR characters only
		const isMicr = new RegExp(this.micrPattern).test(key);
		if (isMicr) {
			return;
		} else {
			e.preventDefault();
		}
	}

	/**
	 * Test whether value is a valid MICR string or not
	 *
	 * @param value
	 */
	validateValue(value: string): void {
		// when MICR value is invalid, strip non-applicable characters
		const valid = new RegExp(this.micrPattern).test(value);
		if (valid) {
			this.hostElement.nativeElement['value'] = value;
		} else {
			this.hostElement.nativeElement['value'] = value.replace(/[^0-9a-d]/g, '');
		}
	}
}
